import type { BotStatus } from '../domain/types';
import type { TranslationKey, useI18n } from '../i18n';

// Actividad del bot traducible (doc 12 · 0.3.0 p4/p7): el cliente reporta un
// CÓDIGO neutro + parámetros (status_code/status_params) y aquí se convierte al
// idioma de la UI, formateando los tiempos como h/min/s (nunca "2295s"). Si el
// código no se conoce (cliente más nuevo que la web) se cae al `text` reportado.

type Translate = ReturnType<typeof useI18n>['t'];

export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return m > 0 ? `${h}h ${m}min` : `${h}h`;
  if (m > 0) return sec > 0 ? `${m}min ${sec}s` : `${m}min`;
  return `${sec}s`;
}

// Claves internas del cliente → claves i18n ya existentes de la web.
const RESOURCE_I18N: Record<string, TranslationKey> = {
  carne: 'settings.resources.meat',
  madera: 'settings.resources.wood',
  carbon: 'settings.resources.coal',
  hierro: 'settings.resources.iron'
};
const UNIT_I18N: Record<string, TranslationKey> = {
  infantry: 'settings.units.infantry',
  lancer: 'settings.units.lancer',
  marksman: 'settings.units.marksman'
};
const MODULE_I18N: Record<string, TranslationKey> = {
  collect: 'dashboard.collect',
  train_troops: 'dashboard.trainTroops',
  auto_hunt: 'dashboard.autoHunt'
};
const HUNT_I18N: Record<string, TranslationKey> = {
  beast: 'settings.beasts',
  terror: 'settings.terror'
};

function translateList(raw: string, map: Record<string, TranslationKey>, t: Translate): string {
  return raw
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => (map[item] ? t(map[item]) : item))
    .join(', ');
}

function translateTask(task: string, t: Translate): string {
  const key = `statusTask.${task}` as TranslationKey;
  const text = t(key);
  return text !== key ? text : task;
}

// Forma mínima que necesita describeStatus: sirve para BotStatus (dashboard)
// y para BotSummary (tarjetas de la flota, 0.6) sin duplicar lógica.
type StatusLike = Pick<BotStatus, 'text' | 'statusCode' | 'statusParams'>;

export function describeStatus(status: StatusLike | undefined | null, t: Translate): string {
  if (!status) return '—';
  const code = status.statusCode;
  if (code) {
    const rawParams = status.statusParams ?? {};
    const effective = code === 'running' && !String(rawParams.modules ?? '').trim() ? 'running_none' : code;
    const key = `status.${effective}` as TranslationKey;
    const params: Record<string, string> = {};
    for (const [name, value] of Object.entries(rawParams)) {
      if (name === 'seconds') params.time = formatDuration(Number(value));
      else if (name === 'units') params.units = translateList(String(value), UNIT_I18N, t);
      else if (name === 'modules') params.modules = translateList(String(value), MODULE_I18N, t);
      else if (name === 'resource') params.resource = translateList(String(value), RESOURCE_I18N, t);
      else if (name === 'type') params.type = translateList(String(value), HUNT_I18N, t);
      else if (name === 'task') params.task = translateTask(String(value), t);
      else params[name] = String(value);
    }
    const text = t(key, params);
    if (text !== key) return text; // código conocido → traducido
  }
  return status.text || '—';
}

export function stateLabel(state: string, t: Translate): string {
  const key = `state.${state}` as TranslationKey;
  const text = t(key);
  return text !== key ? text : state;
}

// Tiempo relativo compacto ("9s", "3min", "2h", "5d"). Compartido por el panel
// de Acciones (último reporte) y las tarjetas de Intelligence (doc 13 §8).
export function formatRelative(iso: string | null | undefined, t: Translate): string {
  if (!iso) return t('dashboard.never');
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) return t('dashboard.never');
  const seconds = Math.max(0, Math.round((Date.now() - at) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}
